import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ButtonUI } from "../../../../components/Button";
import { PageTitle } from "../../../../components/PageTitle";
import {
  useDeleteOrganizationRestApi,
  useOrganizationRestApi,
  useOrganizationUsersRestApi,
} from "../../../../hooks/useOrganizationsRestApi";
import { EditOrganizationModal } from "./components/EditOrganizationModal";
import { OrganizationInfo } from "./components/OrganizationInfo";
import { UsersInOrganization } from "./components/UsersInOrganization";

export const OrganizationDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const organizationId = id;
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { deleteOrganization } = useDeleteOrganizationRestApi();

  const {
    data: orgData,
    loading: orgLoading,
    error: orgError,
    refetch: refetchOrg,
  } = useOrganizationRestApi({
    id: organizationId!,
    skip: !organizationId,
  });

  const {
    data: usersData,
    loading: usersLoading,
    error: usersError,
  } = useOrganizationUsersRestApi({
    organizationId: organizationId!,
    skip: !organizationId,
  });

  const organization = orgData?.organization;
  const users = usersData?.organizationUsers || [];
  const isLoading = orgLoading || usersLoading;
  const error = orgError || usersError;

  // Convert REST API types to admin types for compatibility
  const convertedOrganization = organization
    ? {
        ...organization,
        description: organization.description || undefined,
        employeeCount: users.length, // 実際のユーザー数を使用
        createdAt: organization.createdAt,
        updatedAt: organization.updatedAt,
      }
    : null;

  const handleUpdateOrganization = () => {
    refetchOrg();
    setIsEditModalOpen(false);
  };

  const handleDeleteOrganization = async () => {
    if (!convertedOrganization) return;

    const confirmMessage = `組織「${convertedOrganization.name}」を削除してもよろしいですか？\n\nこの操作は元に戻すことができません。`;

    if (window.confirm(confirmMessage)) {
      try {
        await deleteOrganization(convertedOrganization.id);

        navigate("/admin/organizations");
      } catch (_err) {
        alert("削除に失敗しました");
      }
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-center items-center min-h-64">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">読み込み中...</span>
        </div>
      </div>
    );
  }

  if (error || !convertedOrganization) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {error?.message || "Organizationが見つかりません"}
          </h3>
          <div className="space-x-3">
            <Link
              to="/admin/organizations"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              組織一覧に戻る
            </Link>
            <ButtonUI
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              再読み込み
            </ButtonUI>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* ヘッダー */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <nav className="flex items-center text-sm text-gray-500 mb-2">
            <Link to="/admin" className="hover:text-gray-700">
              管理者ダッシュボード
            </Link>
            <span className="mx-2">/</span>
            <Link to="/admin/organizations" className="hover:text-gray-700">
              組織管理
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{convertedOrganization.name}</span>
          </nav>
          <PageTitle>{convertedOrganization.name}</PageTitle>
          <p className="text-gray-600 mt-2">組織詳細情報と所属ユーザーの管理</p>
        </div>
        <div className="flex space-x-3">
          <ButtonUI
            onClick={() => setIsEditModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            編集
          </ButtonUI>
          <ButtonUI
            onClick={handleDeleteOrganization}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            削除
          </ButtonUI>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Organization情報 */}
        <div className="lg:col-span-1">
          <OrganizationInfo organization={convertedOrganization} />
        </div>

        {/* 所属ユーザー一覧 */}
        <div className="lg:col-span-2">
          <UsersInOrganization
            users={users}
            organizationId={organizationId!}
            organizationName={convertedOrganization.name}
            onRefresh={() => window.location.reload()}
          />
        </div>
      </div>

      {/* 編集モーダル */}
      <EditOrganizationModal
        isOpen={isEditModalOpen}
        organization={organization!}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleUpdateOrganization}
      />
    </div>
  );
};
